import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { formatDateTime } from '@juvare/common-web-ui/utils';
import { useFunctionClient } from '@juvare/platform-sdk-react/ui/hooks';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Typography } from '@mui/material';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { showDeleteDialog, showRestoreDialog } from '../components';
import { renderFileNameColumn, renderFileTypeColumn } from '../utils';
import { renderOriginalLocationColumn } from '../utils/renderOriginalLocationColumn';
import { useDocumentLibraryStorage } from './useDocumentLibraryStorage';
import { useDocumentLibraryTranslations } from './useDocumentLibraryTranslations';
export function useArchivedFileColumns({ defaultPermissions, clearActiveRow, navigate, reload, }) {
    const { t } = useDocumentLibraryTranslations();
    const [searchParams] = useSearchParams();
    const functionClient = useFunctionClient();
    const storage = useDocumentLibraryStorage();
    const getMenuItems = React.useCallback((row) => {
        const menuItems = [];
        const canModify = row.permissions.includes('modify');
        if (canModify) {
            const fileEntityType = row.columns.directory ? t('common.folder') : t('common.file');
            menuItems.push({
                label: t('common.restore'),
                onClick: () => showRestoreDialog({ storage, t, functionClient, file: row, fileEntityType, onSuccess: reload }),
                'data-test': 'menu-item-restore-file',
            });
            menuItems.push({
                label: t('common.delete'),
                onClick: () => {
                    showDeleteDialog({ t, functionClient, file: row, onSuccess: reload });
                },
                'data-test': 'menu-item-delete-file',
            });
        }
        return menuItems;
    }, [functionClient, reload, t, storage]);
    return React.useMemo(() => [
        {
            sortKey: 'name',
            headerLabel: t('documentPanel.name'),
            cellFunction: (row) => (React.createElement(Box, { sx: {
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                } },
                React.createElement(React.Fragment, null, renderFileTypeColumn(t, row, defaultPermissions, clearActiveRow, navigate, searchParams)),
                React.createElement(React.Fragment, null, renderFileNameColumn(t, row, navigate, searchParams)))),
            tableCellHeadSx: {
                paddingLeft: 4,
            },
        },
        {
            sortKey: 'ownerDisplayName',
            headerLabel: t('documentPanel.owner'),
            cellFunction: (row) => React.createElement(Typography, { sx: { wordBreak: 'break-word' } }, row.columns.ownerDisplayName),
            tableCellSx: {
                display: {
                    xs: 'none',
                    sm: 'table-cell',
                },
            },
        },
        {
            headerLabel: t('documentPanel.archiveDate'),
            cellFunction: (row) => {
                const archiveDate = row.columns.archiveDate;
                return archiveDate ? React.createElement(Typography, null, formatDateTime(new Date(archiveDate))) : undefined;
            },
            tableCellSx: {
                display: {
                    xs: 'none',
                    sm: 'table-cell',
                },
            },
        },
        renderOriginalLocationColumn(t),
        {
            cellFunction: (row) => {
                const menuItems = getMenuItems(row);
                if (menuItems.length === 0) {
                    return null;
                }
                return (React.createElement(MenuIconButton, { icon: React.createElement(MoreHorizIcon, null), menuItems: menuItems.map((item) => ({
                        label: item.label,
                        onClick: item.onClick,
                        'data-test': item['data-test'],
                    })), stopPropagationOnClick: true, "data-test": 'more-button', "aria-label": t('documentPanel.openMenu') }));
            },
            tableCellSx: {
                width: 72,
            },
        },
    ], [t, clearActiveRow, defaultPermissions, getMenuItems, navigate, searchParams]);
}
